import {
  FileUploadSettingPiclibErrorMessages,
  PiclibFileUploadMediaFormat as PiclibFileUploadMediaFormatBackend,
} from 'backend';
import isAfter from 'date-fns/isAfter';
import {
  FileUploadThumbnailType,
  FILE_UPLOAD_THUMBNAIL_TYPE,
  mappedConst,
} from 'shared';
import { FileUploadSessionStateReady } from 'store';

export type FileUploadSettingErrorMessageKey =
  keyof FileUploadSettingPiclibErrorMessages;

export const FILE_UPLOAD_ERROR_MESSAGE_KEY: mappedConst<FileUploadSettingErrorMessageKey> =
  {
    /**
     * JavaScriptの対応バージョンが古い、またはプライベートブラウズ機能によりファイル投稿を利用できないときのエラーメッセージ
     */
    UNACCEPTABLE_MEDIA_FORMAT: 'UNACCEPTABLE_MEDIA_FORMAT',
    /**
     * ファイル容量制限違反エラーメッセージ
     */
    FILE_SIZE_TOO_LARGE: 'FILE_SIZE_TOO_LARGE',
    /**
     * ファイルが空（0バイト）のときのエラーメッセージ
     */
    FILE_EMPTY: 'FILE_EMPTY',
    /**
     * ファイルアップロードに関するその他のエラーメッセージ
     */
    UPLOAD_ERROR: 'UPLOAD_ERROR',
    /**
     * ファイル削除に関するエラーメッセージ
     */
    DELETE_ERROR: 'DELETE_ERROR',
    /**
     * セッション時間切れエラーメッセージ
     */
    API_ACCESS_DENIED: 'API_ACCESS_DENIED',
    /**
     * 変更フォームでアップロード済ファイルの情報（サムネイルURL、ファイル種別、オリジナルファイル名）取得に失敗（500エラー）した場合のエラーメッセージ
     */
    GET_UPLOADED_FILE_INFO_ERROR: 'GET_UPLOADED_FILE_INFO_ERROR',
    /**
     * ファイルアップロード以外のその他のエラーメッセージ
     */
    ERROR: 'ERROR',
  };

export const toLowerCamelCaseErrorType = (
  key: FileUploadSettingErrorMessageKey,
) => key.toLowerCase().replace(/_./g, (m) => m[1].toUpperCase());

export const isFileUploadSessionExpired = (
  fileUploadState: FileUploadSessionStateReady,
): boolean => {
  const { expiration } = fileUploadState.submissionSession;
  const now = new Date();
  const expiredAt = new Date(expiration);
  return isAfter(now, expiredAt);
};

export type PiclibFileUploadMediaFormat = PiclibFileUploadMediaFormatBackend;

export const PICLIB_FILE_UPLOAD_MEDIA_FORMAT: mappedConst<PiclibFileUploadMediaFormat> =
  {
    JPEG: 'JPEG',
    PNG: 'PNG',
    GIF: 'GIF',
    PDF: 'PDF',
    MS_WORD_DOCUMENT: 'MS_WORD_DOCUMENT',
    MS_WORD_2007_DOCUMENT: 'MS_WORD_2007_DOCUMENT',
    MS_EXCEL_WORKBOOK: 'MS_EXCEL_WORKBOOK',
    MS_EXCEL_2007_WORKBOOK: 'MS_EXCEL_2007_WORKBOOK',
    MS_POWER_POINT_PRESENTATION: 'MS_POWER_POINT_PRESENTATION',
    MS_POWER_POINT_2007_PRESENTATION: 'MS_POWER_POINT_2007_PRESENTATION',
  };

export const getThumbnailTypeFromPiclibFileUploadMediaFormat = (
  mediaFormat: PiclibFileUploadMediaFormat,
): FileUploadThumbnailType => {
  switch (mediaFormat) {
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.JPEG:
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.PNG:
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.GIF: {
      return FILE_UPLOAD_THUMBNAIL_TYPE.image;
    }
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.PDF: {
      return FILE_UPLOAD_THUMBNAIL_TYPE.pdf;
    }
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.MS_WORD_DOCUMENT:
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.MS_WORD_2007_DOCUMENT: {
      return FILE_UPLOAD_THUMBNAIL_TYPE.word;
    }
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.MS_EXCEL_WORKBOOK:
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.MS_EXCEL_2007_WORKBOOK: {
      return FILE_UPLOAD_THUMBNAIL_TYPE.excel;
    }
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.MS_POWER_POINT_PRESENTATION:
    case PICLIB_FILE_UPLOAD_MEDIA_FORMAT.MS_POWER_POINT_2007_PRESENTATION: {
      return FILE_UPLOAD_THUMBNAIL_TYPE.powerPoint;
    }
  }
};
